'use client';

import { useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { subscribeToNothing } from '../../utils/subscribeToNothing/subscribeToNothing';

export type PortalProps = {
  /**
   * The children to render into the `document.body`.
   */
  children?: ReactNode;
};

/**
 * Portals provide a way to render children into a DOM node
 * that exists outside the DOM hierarchy of the parent component.
 *
 * The portal component renders its children into a new "subtree" outside of current DOM hierarchy.
 */
export const Portal = ({ children }: PortalProps) => {
  // The server has no document and hydration renders what the server rendered, so the portal appears right after hydration.
  const mountNode = useSyncExternalStore(
    subscribeToNothing,
    () => document.body,
    () => null,
  );

  return mountNode ? createPortal(children, mountNode) : null;
};
